import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CustomOptionSecurityType, optionsTradesDataType, optionTradeDataType } from '../pages/typings/options.typings'
import { OptionExpiriesProto } from '../proto/generated/candle-data-depot-option-chain'
import { getSecurityKey } from '../util/security'
import { Expiry } from '../proto/generated/common'
import { Security } from '../proto/generated/candle-data-depot-common'
import store from '../store'
import { SimulatorStateType } from './simulator.reducer'
import { OptionChainViewKey } from '../assets/constants'
import { findSimulatorSquareOffOrder } from '../util/options'

export type OptionType = {
    strike: number
    optionType: 'CE' | 'PE'
}

export interface OptionExpiryType extends Expiry {
    expirationDate: string
    derived: boolean
    atmStrike: number
    ivxExpiry: number
    pcrExpiry: number
}

export interface DropDownStrike {
    strike: number
    callSecurity: Security
    putSecurity: Security
}

export interface DropDownExpiries {
    expiry: Expiry
    strikes: DropDownStrike[]
    strikesArray: number[]
    derived: boolean
}

export interface OptionsStateType {
    drawerOpen: boolean
    tradeData: Record<string, optionsTradesDataType[]>
    underlyingPrice: Record<string, {
        underlyingCurrentPrice: number;
        underlyingLastDayClosingPrice?: number | undefined;
    }>
    expiries: Record<string, OptionExpiryType[]>
    dropdownPastExpiries: Record<string, DropDownExpiries[]>
    dropdownExpiries: Record<string, DropDownExpiries[]>
    dropdownExpiriesExcludedDerived: Record<string, DropDownExpiries[]>
    expiryToStrikesMap: Record<string, Record<string, string[]>> // for deriving expiry from key
    optionToStrikeMap: Record<string, Record<string, OptionType>> // for deriving strike and option type from key
    strikeMap: Record<string, Record<string, optionTradeDataType>> // for deriving corresponding option securities from strike and expiry
    simulatorOrdersLiveData: Record<string, CustomOptionSecurityType|undefined>
}

const initialState: OptionsStateType = {
    drawerOpen: false,
    tradeData: {},
    underlyingPrice: {},
    expiries: {},
    dropdownPastExpiries: {},
    dropdownExpiries: {},
    dropdownExpiriesExcludedDerived: {},
    expiryToStrikesMap: {},
    optionToStrikeMap: {},
    strikeMap: {},
    simulatorOrdersLiveData: {},
}

export const setOptionChainDataFunction = (payload: {
    token: string
    tradeData: optionsTradesDataType[]
    initialLoad: boolean
    underlyingPrice: {
        underlyingCurrentPrice: number
        underlyingLastDayClosingPrice?: number | undefined
    }
    opitonChainViewKey: OptionChainViewKey
}) => {
    const { token, tradeData, initialLoad, underlyingPrice, opitonChainViewKey } = payload
    const expiryToStrikesMap: Record<string, string[]> = {},
        optionToStrikeMap: Record<string, OptionType> = {},
        strikeMap: Record<string, optionTradeDataType> = {}
    const expiriesData: OptionExpiryType[] = []
    tradeData.forEach((expiry) => {
        if (!expiry.expirySeconds) return
        const expiryStrikes: string[] = []

        expiry.trades.forEach(str => {
            strikeMap[`${expiry.expiration.expiry?.expiry}-${str.strikeCol.strike}`] = str
            const ce = str.callOption?.security
            if (ce) {
                const key = getSecurityKey(ce.exchange, ce.code)
                expiryStrikes.push(key)
                optionToStrikeMap[key] = {
                    strike: str.strikeCol.strike,
                    optionType: 'CE'
                }
            }
            const pe = str.putOption?.security
            if (pe) {
                const key = getSecurityKey(pe.exchange, pe.code)
                expiryStrikes.push(key)
                optionToStrikeMap[key] = {
                    strike: str.strikeCol.strike,
                    optionType: 'PE'
                }
            }
        })

        expiryToStrikesMap[expiry.expirySeconds + '-' + expiry.expiration.name] = expiryStrikes
        if (initialLoad) {
            expiriesData.push({
                ...expiry.expiration.expiry as Expiry,
                expirationDate: expiry.expiration.expirationDate,
                derived: expiry.derived,
                atmStrike: expiry.atmStrike,
                ivxExpiry: expiry.ivxExpiry,
                pcrExpiry: expiry.pcrExpiry,
            })
        }
    })
    let simulatorOrdersLiveData: Record<string, CustomOptionSecurityType | undefined> = {}
    if(opitonChainViewKey===OptionChainViewKey.HISTORICAL) {
        const data = calculateSimulatorOrdersLiveData(token, true, strikeMap)
        if(data) simulatorOrdersLiveData = data
    }
    store.dispatch(
        setOptionChainData({
            token,
            tradeData,
            initialLoad,
            underlyingPrice,
            expiriesData,
            expiryToStrikesMap,
            optionToStrikeMap,
            strikeMap,
            simulatorOrdersLiveData
        })
    )
}

export const calculateSimulatorOrdersLiveData = (token: string, returnData?: boolean, strikeMapPassed?: Record<string, optionTradeDataType>) => {
    const { orders, timestamp } = store.getState().simulatorStore as SimulatorStateType
    const strikeMap: Record<string, optionTradeDataType> = strikeMapPassed ? strikeMapPassed : (store.getState().optionsStore as OptionsStateType).strikeMap[token]
    const simulatorOrdersLiveData: Record<string, CustomOptionSecurityType | undefined> = {}
    orders.forEach((order, i) => {
        if(order.contractType === 'OPTION') {
            const callPutKey = order.callput === 'call' ? 'callOption' : 'putOption'
            const sqOffOrder = findSimulatorSquareOffOrder(order, orders, timestamp)
            if (sqOffOrder) {
                    simulatorOrdersLiveData[order.uuid] = (sqOffOrder.data as optionTradeDataType)[callPutKey]!
            } else {
                const key = `${order.expiry}-${order.security?.strikePrice}`
                const strikeToOption = strikeMap[key]
                simulatorOrdersLiveData[order.uuid] = (strikeToOption)?.[callPutKey]
            }
        }
    })
    if(returnData) {
        return simulatorOrdersLiveData
    } else {
        store.dispatch(setSimulatorOrderDataLive(simulatorOrdersLiveData))
    }
}

const optionsSlice = createSlice({
    name: 'options',
    initialState,
    reducers: {
        setOptionChainData(state: OptionsStateType, action: PayloadAction<{
            token: string
            tradeData: optionsTradesDataType[]
            initialLoad: boolean
            underlyingPrice: {
                underlyingCurrentPrice: number
                underlyingLastDayClosingPrice?: number | undefined
            }
            expiriesData: OptionExpiryType[]
            expiryToStrikesMap: Record<string, string[]>
            optionToStrikeMap: Record<string, OptionType>
            strikeMap: Record<string, optionTradeDataType>
            simulatorOrdersLiveData: Record<string, CustomOptionSecurityType|undefined>
        }>) {
            const { token, tradeData, initialLoad, underlyingPrice, expiriesData, expiryToStrikesMap: expiryToStrikesMapNew, optionToStrikeMap: optionToStrikeMapNew, strikeMap: strikeMapNew, simulatorOrdersLiveData } = action.payload

            const expiryToStrikesMap: Record<string, string[]> = { ...state.expiryToStrikesMap[token], ...expiryToStrikesMapNew },
                optionToStrikeMap: Record<string, OptionType> = { ...state.optionToStrikeMap[token], ...optionToStrikeMapNew },
                strikeMap: Record<string, optionTradeDataType> = { ...state.strikeMap[token], ...strikeMapNew }
            return {
                ...state,
                tradeData: {
                    ...state.tradeData,
                    [token]: [...tradeData]
                },
                expiries: initialLoad ? {
                    ...state.expiries,
                    [token]: expiriesData
                } : state.expiries,
                expiryToStrikesMap: {
                    ...state.expiryToStrikesMap,
                    [token]: expiryToStrikesMap
                },
                optionToStrikeMap: {
                    ...state.optionToStrikeMap,
                    [token]: optionToStrikeMap
                },
                strikeMap: {
                    ...state.strikeMap,
                    [token]: strikeMap
                },
                underlyingPrice: {
                    ...state.underlyingPrice,
                    [token]: underlyingPrice
                },
                simulatorOrdersLiveData
            }
        },
        setDropdownPastOptionExpiries(state: OptionsStateType, action: PayloadAction<{
            token: string,
            optionExpiries: OptionExpiriesProto,
        }>) {

            const { token, optionExpiries: pastOptionExpiries } = action.payload
            const underlying = pastOptionExpiries.security
            if (!underlying) return state
            const expiryToStrikesMap: Record<string, string[]> = { ...state.expiryToStrikesMap[token] }
            const optionToStrikeMap: Record<string, OptionType> = { ...state.optionToStrikeMap[token] }
            // strikeMap: Record<string, Strike> = { ...state.strikeMap[token] }
            const dropdownExpiries: DropDownExpiries[] = []
            pastOptionExpiries.expiryStrikes.forEach((expiryData) => {
                const expiryStrikes: string[] = []
                if (expiryData.expiry) {
                    if (expiryData.derived) {
                        dropdownExpiries.push({
                            expiry: expiryData.expiry,
                            derived: expiryData.derived,
                            strikes: [{
                                strike: 0,
                                callSecurity: {
                                    exchange: underlying.exchange,
                                    code: expiryData.securityCodePrefix + 'CE'
                                },
                                putSecurity: {
                                    exchange: underlying.exchange,
                                    code: expiryData.securityCodePrefix + 'PE'
                                }
                            }],
                            strikesArray: [0]
                        })
                        expiryStrikes.push(getSecurityKey(underlying.exchange, expiryData.securityCodePrefix + 'CE'))
                        expiryStrikes.push(getSecurityKey(underlying.exchange, expiryData.securityCodePrefix + 'PE'))
                    } else {
                        const data: DropDownExpiries = {
                            expiry: expiryData.expiry,
                            derived: expiryData.derived,
                            strikes: expiryData.strikes.map(strike => {
                                const strikeObj = {
                                    strike: strike,
                                    callSecurity: {
                                        exchange: underlying.exchange,
                                        code: expiryData.securityCodePrefix + strike + '_CE'
                                    },
                                    putSecurity: {
                                        exchange: underlying.exchange,
                                        code: expiryData.securityCodePrefix + strike + '_PE'
                                    }
                                }
                                const callOptionKey = getSecurityKey(underlying.exchange, expiryData.securityCodePrefix + strike + '_CE')
                                const putOptionKey = getSecurityKey(underlying.exchange, expiryData.securityCodePrefix + strike + '_PE')
                                optionToStrikeMap[callOptionKey] = {
                                    strike: strike,
                                    optionType: 'CE'
                                }
                                optionToStrikeMap[putOptionKey] = {
                                    strike: strike,
                                    optionType: 'PE'
                                }
                                expiryStrikes.push(callOptionKey)
                                expiryStrikes.push(putOptionKey)
                                return strikeObj
                            }),
                            strikesArray: expiryData.strikes
                        }
                        dropdownExpiries.push(data)
                    }
                    expiryToStrikesMap[expiryData.expiry.expiry + '-' + expiryData.expiry.name] = expiryStrikes
                }

            })
            return {
                ...state,
                dropdownPastExpiries: {
                    ...state.dropdownPastExpiries,
                    [token]: dropdownExpiries
                },
                expiryToStrikesMap: {
                    ...state.expiryToStrikesMap,
                    [token]: expiryToStrikesMap
                },
                optionToStrikeMap: {
                    ...state.optionToStrikeMap,
                    [token]: optionToStrikeMap
                },
                // strikeMap: {
                //     ...state.strikeMap,
                //     [token]: strikeMap
                // }
            }
        },
        setDropdownOptionExpiries(state: OptionsStateType, action: PayloadAction<{
            token: string,
            dropdownExpiries: DropDownExpiries[],
        }>) {
            const { token, dropdownExpiries } = action.payload
            const optionToStrikeMap: Record<string, OptionType> = { ...state.optionToStrikeMap[token] }
            dropdownExpiries.forEach((expiry) => {
                expiry.strikes.forEach(str => {
                    const ce = str.callSecurity
                    if (ce) {
                        const key = getSecurityKey(ce.exchange, ce.code)
                        optionToStrikeMap[key] = {
                            strike: str.strike,
                            optionType: 'CE'
                        }
                    }
                    const pe = str.putSecurity
                    if (pe) {
                        const key = getSecurityKey(pe.exchange, pe.code)
                        optionToStrikeMap[key] = {
                            strike: str.strike,
                            optionType: 'PE'
                        }
                    }
                })
            })

            return {
                ...state,
                dropdownExpiries: {
                    ...state.dropdownExpiries,
                    [token]: dropdownExpiries
                },
                dropdownExpiriesExcludedDerived: {
                    ...state.dropdownExpiriesExcludedDerived,
                    [token]: dropdownExpiries.filter(d => !d.derived)
                },
                optionToStrikeMap: {
                    ...state.optionToStrikeMap,
                    [token]: optionToStrikeMap
                }
            }
        },
        setSimulatorOrderDataLive(state: OptionsStateType, action: PayloadAction<Record<string, CustomOptionSecurityType | undefined>>) {
            return {
                ...state,
                simulatorOrdersLiveData: action.payload
            }
        },
        clearOptionChain(state: OptionsStateType) {
            return {
                ...state,
                tradeData: {},
                expiries: {},
                expiryToStrikesMap: {},
                optionToStrikeMap: {},
                strikeMap: {},
                underlyingPrice: {},
                simulatorOrdersLiveData: {}
            }
        },
        setOptionChainDrawerOpen(state: OptionsStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                drawerOpen: action.payload
            }
        }
    }
})

const { actions, reducer } = optionsSlice

export const { setOptionChainData, setDropdownOptionExpiries, setDropdownPastOptionExpiries, setSimulatorOrderDataLive, clearOptionChain, setOptionChainDrawerOpen } = actions

export default reducer